/**
 * Branded primitive types.
 *
 * 目的：避免「分鐘 / 小時 / 天」「日期 / 日期時間」「金額 / 純數字」互相誤用。
 * 這些 brand 只存在於型別層，執行期就是 number / string，零 runtime 成本。
 */

declare const __brand: unique symbol;

export type Brand<TBase, TBrand extends string> = TBase & {
  readonly [__brand]: TBrand;
};

// ---------------------------------------------------------------------------
// 量值單位
// ---------------------------------------------------------------------------

/** 分鐘。專案中所有工期、工時、日曆時段一律以分鐘為唯一內部單位。 */
export type Minutes = Brand<number, 'Minutes'>;

/** 金額。以專案幣別為單位的浮點數。 */
export type Money = Brand<number, 'Money'>;

/**
 * 資源投入單位。1 = 100%（MS Project 的 Units）。
 * 一個資源以 0.5 投入代表半時；maxUnits = 2 代表兩人份的資源池。
 */
export type Units = Brand<number, 'Units'>;

/** 百分比，0–100。用於 percentComplete / percentWorkComplete。 */
export type Percent = Brand<number, 'Percent'>;

/** 每分鐘費率（Money / Minutes）。標準／加班費率一律換算為每分鐘，避免時薪日薪混用。 */
export type RatePerMinute = Brand<number, 'RatePerMinute'>;

// ---------------------------------------------------------------------------
// 時間表示
// ---------------------------------------------------------------------------

/** 純日期，格式 `YYYY-MM-DD`（不帶時區）。 */
export type ISODate = Brand<string, 'ISODate'>;

/** 日期時間，格式 `YYYY-MM-DDTHH:mm:ss`（local wall-clock，不帶時區位移）。 */
export type ISODateTime = Brand<string, 'ISODateTime'>;

// ---------------------------------------------------------------------------
// 實體識別碼
// ---------------------------------------------------------------------------

export type Id<TEntity extends string> = Brand<string, `${TEntity}Id`>;

export type ProjectId = Id<'Project'>;
export type TaskId = Id<'Task'>;
export type DependencyId = Id<'Dependency'>;
export type ResourceId = Id<'Resource'>;
export type AssignmentId = Id<'Assignment'>;
export type CalendarId = Id<'Calendar'>;
export type TimesheetId = Id<'Timesheet'>;
export type CustomFieldId = Id<'CustomField'>;

// ---------------------------------------------------------------------------
// 建構子 / 轉換 helper（全部為零成本 cast，不做 I/O）
// ---------------------------------------------------------------------------

export const minutes = (value: number): Minutes => value as Minutes;
export const money = (value: number): Money => value as Money;
export const units = (value: number): Units => value as Units;
export const percent = (value: number): Percent => value as Percent;
export const ratePerMinute = (value: number): RatePerMinute => value as RatePerMinute;

export const isoDate = (value: string): ISODate => value as ISODate;
export const isoDateTime = (value: string): ISODateTime => value as ISODateTime;

export const id = <TEntity extends string>(value: string): Id<TEntity> =>
  value as Id<TEntity>;

export const taskId = (value: string): TaskId => value as TaskId;
export const resourceId = (value: string): ResourceId => value as ResourceId;
export const calendarId = (value: string): CalendarId => value as CalendarId;
export const assignmentId = (value: string): AssignmentId => value as AssignmentId;
export const dependencyId = (value: string): DependencyId => value as DependencyId;
export const projectId = (value: string): ProjectId => value as ProjectId;
export const customFieldId = (value: string): CustomFieldId => value as CustomFieldId;

/** 剝除 brand，取回原始數值。 */
export const rawNumber = (value: Brand<number, string>): number => value as number;
/** 剝除 brand，取回原始字串。 */
export const rawString = (value: Brand<string, string>): string => value as string;

// ---------------------------------------------------------------------------
// 常用換算常數（僅為方便，不代表日曆工時）
// ---------------------------------------------------------------------------

export const MINUTES_PER_HOUR = 60;
export const MINUTES_PER_24H = 1440;

export const fromHours = (hours: number): Minutes => (hours * MINUTES_PER_HOUR) as Minutes;
export const toHours = (value: Minutes): number => (value as number) / MINUTES_PER_HOUR;

/** 由時薪換算為每分鐘費率。 */
export const rateFromHourly = (hourlyRate: number): RatePerMinute =>
  (hourlyRate / MINUTES_PER_HOUR) as RatePerMinute;
